/**
 * Copyright 2021 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import React, { useState, useRef } from 'react'
import { ChevronDown, ChevronUp, Hidden, Icon, View } from '@az/starc-ui'
import { useOutsideTracker } from '../../helpers/hooks/useOutsideTracker'

interface ICustomMenuProps {
  id?: string
  menuTriggerLabel: React.ReactElement | string
  children: React.ReactElement
  closeWhenClick?: boolean
  showHover?: boolean
  width?: string
}

export const CustomMenu: React.FC<ICustomMenuProps> = props => {
  const { menuTriggerLabel, children, id, closeWhenClick = true, showHover, width } = props
  const [clicked, setClicked] = useState(false)
  const [hover, setHover] = useState(false)
  const wrapperRef = useRef(null)

  useOutsideTracker(wrapperRef, () => {
    setClicked(false)
  })

  const handleClick = () => {
    setClicked(prevState => !prevState)
  }

  const handleCloseWhenClickInside = () => {
    closeWhenClick && handleClick()
  }

  return (
    <View
      attributes={{
        ref: wrapperRef,
        style: {
          position: 'relative'
        }
      }}
    >
      <View
        borderRadius="small"
        attributes={{
          id: id,
          onClick: handleClick,
          onMouseOver: () => setHover(true),
          onMouseLeave: () => setHover(false),
          style: {
            cursor: 'pointer',
            backgroundColor: showHover && (hover || clicked) ? 'lightgray' : undefined
          }
        }}
      >
        <View align="center" direction="row" padding={[0, 2]} gap={4}>
          <View.Item grow>{menuTriggerLabel}</View.Item>
          <View.Item>
            {clicked ? <Icon svg={ChevronUp} size={4} /> : <Icon svg={ChevronDown} size={4} />}
          </View.Item>
        </View>
      </View>
      <Hidden hide={!clicked}>
        <View
          shadow="small"
          borderRadius="small"
          backgroundColor="secondary"
          width={width}
          attributes={{
            onClick: handleCloseWhenClickInside,
            style: {
              position: 'absolute',
              top: '50px',
              zIndex: 1000
            }
          }}
        >
          {children}
        </View>
      </Hidden>
    </View>
  )
}
